import { jsxAsElement as jsx } from 'web-shared/src/lib/jsx';
import { apiUrl } from 'web-shared/src/lib/api';
import { getNowListening } from '../lib/plex';

interface PlexNowListening {
  user: string;
  device: string;
  state: 'playing' | 'paused';
  progress: number;
  duration: number;
  albumName: string;
  albumArtist?: string;
  albumYear?: number;
  trackName: string;
  thumbPath?: string;
}

export class NowListening extends HTMLElement {
  currentProgressMs: number;
  interval?: ReturnType<typeof setInterval>;

  constructor() {
    super();
    this.currentProgressMs = 0;
  }

  randomVerb() {
    const verbs = [
      'consuming',
      'engaging with',
      'pondering',
      'smelling',
      'glued to',
      'sleeping to',
      'working to',
      'eating to',
      'enjoying',
      'partying to',
      'programming to',
      'engaged with',
    ];

    return verbs[Math.floor(Math.random() * verbs.length)];
  }

  renderProgress(duration: number) {
    const progressPercent = (this.currentProgressMs / duration) * 100;
    const progressTime = new Date(this.currentProgressMs || 0)
      .toISOString()
      .substring(14, 19);

    return (
      <div className="progress" id="currentProgress">
        {progressTime}
        <div className="bar">
          <div
            className="barContent"
            style={`width: ${progressPercent}%`}
          ></div>
        </div>
      </div>
    );
  }

  handleMarquee(elements: HTMLElement[]) {
    for (const element of elements) {
      if (element) {
        const parentWidth = element.clientWidth;
        const ownWidth = element.scrollWidth;

        if (ownWidth > parentWidth) {
          element.classList.add('marquee');
          const textLength = element.textContent?.length || 0;
          const readingSpeed = 6; // characters per second
          const duration = Math.max(2, textLength / readingSpeed);

          element.setAttribute(
            'style',
            `--marquee-offset: ${parentWidth - ownWidth}px; --marquee-speed: ${duration}s`,
          );
        } else {
          element.classList.remove('marquee');
          element.setAttribute('style', '');
        }
      }
    }
  }

  async renderListening() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = undefined;
    }

    const listeningResult = await getNowListening();
    if (listeningResult.isErr()) {
      console.error('Failed to fetch listening', listeningResult.error);
      return;
    }

    const listening = listeningResult.value;
    if (!listening) {
      this.innerHTML = '';
      return;
    }

    this.currentProgressMs = listening.progress;
    this.interval = setInterval(() => {
      if (!this.isConnected) {
        clearInterval(this.interval);
        return;
      }

      this.currentProgressMs += 1000;
      const progressElement = document.getElementById('currentProgress');
      if (progressElement) {
        progressElement.replaceWith(this.renderProgress(listening.duration));
      }

      if (this.currentProgressMs >= listening.duration) {
        clearInterval(this.interval);
        this.interval = undefined;

        void this.renderListening();
      }
    }, 1000);

    this.innerHTML = '';
    const titleElement = (
      <div className="albumTitle">{listening.trackName}</div>
    );

    const artistElement = (
      <div className="albumArtist">
        {listening.albumName}&nbsp;-&nbsp;{listening.albumArtist}
      </div>
    );

    this.append(
      <div className="nowListening" id="nowListening">
        <p>
          She's currently{' '}
          <a
            href="https://git.pfy.ch/pfych/static/src/commit/5bc3cd25e2798d0b8fbc58c91483b691deb4b71c/packages/site/assets/scripts/components/nowListening.tsx#L28"
            target="_blank"
            class="blank"
          >
            {this.randomVerb()}
          </a>
        </p>
        <div className="content">
          <img
            src="/placeholder.svg"
            data-src={`${apiUrl}${listening.thumbPath}`}
            alt={listening.albumName || 'Album art'}
            onload={(e: Event) => {
              const img = e.target as HTMLImageElement;
              if (img.dataset.src && img.src.endsWith('placeholder.svg')) {
                img.src = img.dataset.src;
              }
            }}
          />
          <div className="listeningDetails">
            <div className="listeningMeta">
              {titleElement}
              {artistElement}
            </div>
            {this.renderProgress(listening.duration)}
          </div>
        </div>
      </div>,
    );

    setTimeout(() => {
      this.handleMarquee([titleElement, artistElement]);
    }, 2500);
  }

  connectedCallback() {
    void this.renderListening();
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }
}
